import { useEffect } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Pings `profiles.last_active_at` every 30 seconds while the customer's
 * tab is visible. Also pings on mount and on visibility change (when the
 * customer comes back to the tab).
 *
 * The admin chat email cron uses this timestamp to skip sending email
 * notifications when the customer is actively online — they'll see the
 * message in-app without needing the email reminder.
 *
 * Cheap update — single column, no joins, RLS-scoped to the user's own
 * profile row. Failures are silent so we never surface a heartbeat error
 * to the user.
 */
export function useActivityHeartbeat(userId: string | null | undefined) {
  useEffect(() => {
    if (!userId) return;
    if (typeof document === 'undefined') return;

    let cancelled = false;

    const ping = async () => {
      if (cancelled) return;
      // Only ping while the tab is actually visible. If the user has the
      // tab in the background, they're not really "active" and the email
      // cron should be free to send notifications.
      if (document.visibilityState !== 'visible') return;
      try {
        await supabase
          .from('profiles')
          .update({ last_active_at: new Date().toISOString() })
          .eq('id', userId);
      } catch {
        // Silent fail — heartbeat errors should never disrupt UX.
      }
    };

    // Initial ping immediately.
    ping();

    // Periodic ping every 30s.
    const interval = setInterval(ping, 30_000);

    // Re-ping the moment the tab becomes visible again (covers the case
    // where the customer was away for an hour, then comes back — we want
    // to update last_active_at right away, not wait up to 30s).
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') ping();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [userId]);
}
